import Backdrop from '@atoms/Backdrop/Backdrop';
import Cta from '@atoms/Cta/Cta';
import Icon from '@atoms/Icon/Icon';
import Portal from '@atoms/Modal/Portal';
import Text from '@atoms/Text/Text';
import SiteEnum from '@models/enums/SiteEnum';
import { ILink } from '@models/ILink';
import { slideTransition } from '@utility/config/animations';
import bodyScrollLock from '@utility/helpers/bodyScrollLock';
import useMediaQuery from '@utility/hooks/useMediaQuery';
import classNames from 'classnames';
import FocusTrap from 'focus-trap-react';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { PropsWithChildren, useEffect, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';

export interface IDrawer {
  readonly animateVerticallyOnMobile?: boolean;
  readonly closeButtonVariant?: 'filled' | 'default';
  readonly isInspirationOverlay?: boolean;
  readonly closeLabel?: string;
  readonly containerClasses?: string;
  readonly cta?: ILink;
  readonly description?: TrustedHTML | string;
  readonly drawerClassnames?: string;
  readonly drawerExpandsToFullHeightOnMobile?: boolean;
  readonly isOpen: boolean;
  readonly isRtl?: boolean;
  readonly key?: string;
  readonly onExitComplete?: () => void;
  readonly preTitle?: string;
  readonly setIsOpen: (state: boolean) => void;
  readonly showAnimations?: boolean;
  readonly site?: SiteEnum;
  readonly sizeVariant?: 'small' | 'large';
  readonly slideFromLeft?: boolean;
  readonly title?: string;
  readonly wrapperStyles?: string;
}

const Drawer = ({
  animateVerticallyOnMobile = true,
  children,
  closeButtonVariant = 'default',
  closeLabel = 'close',
  containerClasses,
  cta,
  description,
  drawerClassnames,
  isInspirationOverlay = false,
  isOpen,
  onExitComplete,
  preTitle,
  setIsOpen,
  showAnimations = true,
  site,
  sizeVariant = 'small',
  slideFromLeft = false,
  title,
  wrapperStyles = 'tw-px-4 tw-py-10 md:tw-p-12',
}: PropsWithChildren<IDrawer>) => {
  const [isRtl, setIsRtl] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const listId = isInspirationOverlay ? 'level-2' : `level-1`;

  const isMobile = useMediaQuery(752);
  const shouldReduceMotion = useReducedMotion();

  const useMobileAnimation = animateVerticallyOnMobile && isMobile;
  const horizontalDir = (slideFromLeft ? -1 : 1) * (isRtl ? -1 : 1);

  const showHeader = title || description || cta;

  const slideIn =
    showAnimations && !shouldReduceMotion
      ? {
          hidden: {
            x: useMobileAnimation ? 0 : `${100 * horizontalDir}%`,
            y: useMobileAnimation ? '100%' : 0,
          },
          animate: { x: 0, y: 0 },
          exit: {
            x: useMobileAnimation ? 0 : `${100 * horizontalDir}%`,
            y: useMobileAnimation ? '100%' : 0,
          },
        }
      : { hidden: {}, animate: {}, exit: {} };

  useEffect(() => {
    bodyScrollLock(isOpen, listId);

    return () => bodyScrollLock(false, listId);
  }, [isOpen]);

  useEffect(() => {
    setIsRtl(document.documentElement.getAttribute('dir') === 'rtl');
  }, []);

  return (
    <AnimatePresence
      initial={false}
      mode="wait"
      onExitComplete={onExitComplete}
    >
      {isOpen && (
        <Portal
          key="portal"
          mountId={
            isInspirationOverlay ? 'inspiration-overlay' : 'portal-mount'
          }
        >
          <FocusTrap
            focusTrapOptions={{
              preventScroll: true,
              initialFocus: () => containerRef.current,
              fallbackFocus: () => containerRef.current as HTMLElement,
            }}
          >
            <div>
              <Backdrop
                className={isInspirationOverlay ? '!tw-z-90' : '!tw-z-100'}
                onCloseCallback={() => setIsOpen(false)}
              />
              <motion.div
                ref={containerRef}
                variants={slideIn}
                initial="exit"
                animate="animate"
                exit="exit"
                transition={{
                  ...slideTransition,
                  duration: shouldReduceMotion ? 0 : 0.8,
                  layout: {
                    duration: shouldReduceMotion ? 0 : 0.8,
                    ease: 'easeInOut',
                  },
                }}
                key="drawer"
                role="dialog"
                aria-modal="true"
                aria-label={title}
                className={twMerge(
                  'tw-fixed tw-z-100 tw-w-full tw-overflow-hidden tw-bg-white tw-outline-none tw-will-change-transform dark:tw-bg-grey-10',
                  site === SiteEnum.B2B && !isMobile
                    ? 'fixed-under-header'
                    : 'tw-top-0 tw-h-dvh',
                  sizeVariant === 'small'
                    ? 'md:tw-max-w-[496px]'
                    : 'md:tw-max-w-[667px]',
                  animateVerticallyOnMobile
                    ? 'max-md:tw-mt-[120px] max-md:tw-h-[calc(100dvh-120px)] [@media(max-height:500px)]:tw-mt-0 [@media(max-height:500px)]:tw-h-[100dvh]'
                    : '',
                  slideFromLeft
                    ? 'tw-left-0 rtl:tw-left-auto rtl:tw-right-0'
                    : 'tw-right-0 rtl:tw-left-0 rtl:tw-right-auto',
                  drawerClassnames
                )}
                tabIndex={0}
              >
                <div className="tw-flex tw-h-full tw-w-full tw-flex-col tw-text-black">
                  {/* Fixed header */}
                  {showHeader ? (
                    <div
                      className={classNames(
                        'tw-sticky tw-top-[-1px] tw-z-50 tw-bg-grey-10 tw-px-4 tw-py-6 md:tw-px-12 md:tw-py-10 [@media(max-height:500px)]:tw-py-1',
                        containerClasses
                      )}
                    >
                      {preTitle && (
                        <Text
                          variant="label-12"
                          className="-tw-mt-1 tw-block tw-text-grey-60 md:tw-mb-1 md:tw-mt-0"
                        >
                          {preTitle}
                        </Text>
                      )}
                      {title && (
                        <Text variant="heading-28" className="tw-my-auto">
                          {title}
                        </Text>
                      )}
                      {description && (
                        <Text
                          className="tw-pt-2 tw-text-grey-80 [@media(max-height:500px)]:tw-pt-0"
                          variant="body-small"
                          dangerouslySetInnerHTML={{ __html: description }}
                        />
                      )}
                      {cta?.text && cta.url && (
                        <div className="tw-pt-6">
                          <Cta {...cta} variant="grey" />
                        </div>
                      )}

                      <button
                        type="button"
                        aria-label={closeLabel}
                        className={classNames(
                          'tw-absolute tw-top-xs tw-z-max',
                          isRtl ? 'tw-left-xs' : 'tw-right-xs'
                        )}
                        onClick={() => setIsOpen(false)}
                      >
                        <Icon
                          name="cross"
                          className="tw-text-2xl"
                          size="1.5rem"
                        />
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      aria-label={closeLabel}
                      className={classNames(
                        'tw-absolute tw-top-xs tw-z-max',
                        isRtl ? ' tw-left-xs' : ' tw-right-xs',
                        closeButtonVariant === 'filled' &&
                          '!tw-right-0 !tw-top-0 tw-bg-white tw-p-3',
                        closeButtonVariant === 'filled' &&
                          isRtl &&
                          '!tw-left-0 !tw-right-auto'
                      )}
                      onClick={() => setIsOpen(false)}
                    >
                      <Icon
                        name="cross"
                        className="tw-text-2xl"
                        size="1.5rem"
                      />
                    </button>
                  )}

                  {/* Scrollable content */}
                  <div
                    className="tw-relative tw-bottom-0 tw-left-0 tw-h-full tw-w-full tw-scroll-mt-[108px] tw-overflow-y-auto"
                    ref={contentRef}
                  >
                    <div className="tw-relative tw-flex tw-h-full tw-w-full tw-flex-col tw-gap-y-lg">
                      <div className={wrapperStyles}>{children}</div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </FocusTrap>
        </Portal>
      )}
    </AnimatePresence>
  );
};

export default Drawer;
